import {
  Box,
  Button,
  Callout,
  Flex,
  Grid,
  Heading,
  Select,
  Separator,
  Text,
  TextArea,
  TextField,
} from "@radix-ui/themes";
import {
  AlertCircle,
  CalendarDays,
  Image,
  Link as LinkIcon,
  Palette,
  Tag,
  Trash2,
} from "lucide-react";
import { SubmitEvent } from "react";
import { ManagedTrack, SeasonFormData } from "@/types/shared";

interface SeasonFormProps {
  data: SeasonFormData;
  setData: <K extends keyof SeasonFormData>(
    key: K,
    value: SeasonFormData[K],
  ) => void;
  onSubmit: (e: SubmitEvent) => void;
  processing: boolean;
  errors?: string[];
  tracks: ManagedTrack[];
  title: string;
  submitLabel: string;
  hasPoster?: boolean;
  /** The attached poster, shown as a thumbnail. Absent on the new-season form. */
  posterUrl?: string | null;
  isEdit?: boolean;
}

export default function SeasonForm({
  data,
  setData,
  onSubmit,
  processing,
  errors,
  tracks,
  title,
  submitLabel,
  hasPoster,
  posterUrl,
  isEdit,
}: SeasonFormProps) {
  const setSeason = (fields: Partial<SeasonFormData["season"]>) => {
    setData("season", { ...data.season, ...fields });
  };

  return (
    <Box className="border-2 py-8 px-10">
      <Heading size="7">{title}</Heading>
      <Text size="4" as="p" color="gray">
        Group a set of events into a championship or series.
      </Text>
      <Separator my="3" size="4" />

      {errors && errors.length > 0 && (
        <Callout.Root color="red" role="alert" mb="4">
          {errors.map((error, index) => (
            <div key={index}>
              <Callout.Icon>
                <AlertCircle />
              </Callout.Icon>
              <Callout.Text>{error}</Callout.Text>
            </div>
          ))}
        </Callout.Root>
      )}

      <form onSubmit={onSubmit}>
        {/* Basics */}
        <Grid columns="1" gap="5" mb="6">
          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">
                Season Name *
              </Text>
              <TextField.Root
                size="3"
                value={data.season.name}
                onChange={(e) => setSeason({ name: e.target.value })}
                required
                placeholder="e.g. 2026 Summer Championship"
              />
            </Flex>
          </Box>

          {!isEdit && (
            <Box>
              <Flex gap="2" direction="column">
                <Text size="3" weight="medium">
                  Track *
                </Text>
                <Select.Root
                  size="3"
                  value={data.season.track_id}
                  defaultValue={data.season.track_id}
                  onValueChange={(v) => setSeason({ track_id: v })}
                  required
                >
                  <Select.Trigger placeholder="Select a track…" />
                  <Select.Content>
                    {tracks.map((track) => (
                      <Select.Item key={track.id} value={track.id.toString()}>
                        {track.name}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </Flex>
            </Box>
          )}

          <Grid columns="2" gap="5">
            <Box>
              <Flex gap="2" direction="column">
                <Text size="3" weight="medium">
                  Start Date *
                </Text>
                <TextField.Root
                  type="date"
                  size="3"
                  value={data.season.start_date}
                  onChange={(e) => setSeason({ start_date: e.target.value })}
                  required
                >
                  <TextField.Slot>
                    <CalendarDays height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
            </Box>

            <Box>
              <Flex gap="2" direction="column">
                <Text size="3" weight="medium">
                  End Date
                </Text>
                <TextField.Root
                  type="date"
                  size="3"
                  value={data.season.end_date}
                  onChange={(e) => setSeason({ end_date: e.target.value })}
                >
                  <TextField.Slot>
                    <CalendarDays height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
            </Box>
          </Grid>
        </Grid>

        {/* Branding */}
        <Heading size="5" weight="bold">
          Branding
        </Heading>
        <Separator my="3" size="4" />
        <Grid columns="1" gap="5" mb="6">
          <Grid columns="2" gap="5">
            <Box>
              <Flex gap="2" direction="column">
                <Text size="3" weight="medium">
                  Title Sponsor
                </Text>
                <TextField.Root
                  size="3"
                  value={data.season.title_sponsor}
                  onChange={(e) => setSeason({ title_sponsor: e.target.value })}
                  placeholder="e.g. Acme Tires"
                >
                  <TextField.Slot>
                    <Tag height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
            </Box>

            <Box>
              <Flex gap="2" direction="column">
                <Text size="3" weight="medium">
                  Accent Color
                </Text>
                <TextField.Root
                  size="3"
                  value={data.season.accent_color}
                  onChange={(e) => setSeason({ accent_color: e.target.value })}
                  placeholder="#ff0080"
                >
                  <TextField.Slot>
                    <Palette height="16" width="16" />
                  </TextField.Slot>
                </TextField.Root>
              </Flex>
            </Box>
          </Grid>

          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">
                Rules URL
              </Text>
              <TextField.Root
                size="3"
                value={data.season.rules_url}
                onChange={(e) => setSeason({ rules_url: e.target.value })}
                placeholder="https://example.com/rules"
              >
                <TextField.Slot>
                  <LinkIcon height="16" width="16" />
                </TextField.Slot>
              </TextField.Root>
            </Flex>
          </Box>
        </Grid>

        {/* Publishing */}
        <Heading size="5" weight="bold">
          Publishing
        </Heading>
        <Separator my="3" size="4" />
        <Grid columns="1" gap="5" mb="6">
          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">
                Status
              </Text>
              <Text size="2" color="gray">
                Drafts are hidden from the public; published seasons appear on
                the track page.
              </Text>
              <Select.Root
                size="3"
                value={data.season.status}
                onValueChange={(v) => setSeason({ status: v })}
              >
                <Select.Trigger />
                <Select.Content>
                  <Select.Item value="draft">Draft</Select.Item>
                  <Select.Item value="published">Published</Select.Item>
                </Select.Content>
              </Select.Root>
            </Flex>
          </Box>
        </Grid>

        {/* Content */}
        <Heading size="5" weight="bold">
          Content
        </Heading>
        <Separator my="3" size="4" />
        <Grid columns="1" gap="5" mb="6">
          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">
                Description
              </Text>
              <TextArea
                size="3"
                rows={5}
                value={data.season.description}
                onChange={(e) => setSeason({ description: e.target.value })}
                placeholder="Tell people what this season is about…"
              />
            </Flex>
          </Box>
        </Grid>

        {/* Poster */}
        <Heading size="5" weight="bold">
          Season Poster
        </Heading>
        <Separator my="3" size="4" />
        <Grid columns="1" gap="5" mb="6">
          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">
                Poster Image
              </Text>
              <Text size="2" color="gray">
                Displayed on the season detail page. Recommended 1200×630px.
              </Text>
              {hasPoster && !data.season.remove_poster && (
                <Flex gap="3" align="center">
                  {posterUrl && (
                    <img
                      src={posterUrl}
                      alt="Current season poster"
                      style={{
                        display: "block",
                        maxWidth: "8rem",
                        maxHeight: "10rem",
                        objectFit: "contain",
                        border: "1px solid var(--gray-5)",
                        borderRadius: "var(--radius-3)",
                      }}
                    />
                  )}
                  <Flex direction="column" gap="1" align="start">
                    <Text size="2" color="indigo">
                      Current poster — upload below to replace it.
                    </Text>
                    <Button
                      type="button"
                      variant="ghost"
                      size="1"
                      color="red"
                      onClick={() =>
                        setSeason({ poster: null, remove_poster: true })
                      }
                    >
                      <Trash2 size={12} />
                      Remove poster
                    </Button>
                  </Flex>
                </Flex>
              )}

              {/* Staged, not immediate: the poster goes when the manager saves,
                  the same as every other change on this form. */}
              {hasPoster && data.season.remove_poster && (
                <Flex gap="2" align="center">
                  <Text size="2" color="red">
                    Poster will be removed when you save.
                  </Text>
                  <Button
                    type="button"
                    variant="ghost"
                    size="1"
                    color="gray"
                    onClick={() => setSeason({ remove_poster: false })}
                  >
                    Undo
                  </Button>
                </Flex>
              )}
              <Flex align="center" gap="2">
                <Image height="16" width="16" />
                <input
                  type="file"
                  accept="image/*"
                  // Choosing a replacement cancels a staged removal — uploading
                  // one and deleting the old one are the same intent.
                  onChange={(e) =>
                    setSeason({
                      poster: e.target.files?.[0] ?? null,
                      remove_poster: false,
                    })
                  }
                  style={{ fontSize: "var(--font-size-2)" }}
                />
              </Flex>
            </Flex>
          </Box>
        </Grid>

        <Box mt="6">
          <Flex gap="4" justify="end">
            <Button
              type="button"
              size="3"
              color="gray"
              variant="soft"
              onClick={() => history.back()}
            >
              Cancel
            </Button>
            <Button size="3" type="submit" disabled={processing}>
              {submitLabel}
            </Button>
          </Flex>
        </Box>
      </form>
    </Box>
  );
}
